/**
 * ============================================================================
 * PARTNER POLICY — GLOBAL ENFORCEMENT MODULE (IMMUTABLE)
 * ============================================================================
 * 
 * Ce module est la SOURCE DE VÉRITÉ UNIQUE pour toute logique d'accès Partner
 * côté frontend. Il remplace et consolide lib/partnerAccessRule.js.
 * 
 * ════════════════════════════════════════════════════════════════════════════
 * ACCESS RULE PARTNER — CONTRAT GLOBAL
 * ════════════════════════════════════════════════════════════════════════════
 * 
 *   Distributor         → TOUJOURS cliquable
 *   Publisher/Supplier   → cliquable UNIQUEMENT si has_users === true
 *   Other/Unknown        → cliquable UNIQUEMENT si has_users === true
 *   null/undefined       → NON cliquable
 * 
 * ════════════════════════════════════════════════════════════════════════════
 * INTERDICTIONS ABSOLUES (vérifiées par CI guard)
 * ════════════════════════════════════════════════════════════════════════════
 * 
 *   ❌ partner.status === ...
 *   ❌ partner.has_users === ... (inline, hors ce module)
 *   ❌ partner.users.length > 0
 *   ❌ partner.verified / partner.is_verified
 *   ❌ partner_status / claim_status
 *   ❌ Toute logique inline de Partner access dans les composants
 * 
 * ════════════════════════════════════════════════════════════════════════════
 * DONNÉES REQUISES (backend enrichment)
 * ════════════════════════════════════════════════════════════════════════════
 * 
 *   - partner.capabilities (array) OU partner.partner_capabilities (array)
 *   - partner.has_users (boolean, calculé côté backend, jamais stocké)
 * 
 * ============================================================================
 */

// ─── ACCESS STATE ENUM ──────────────────────────────────────────────────────

export const PARTNER_ACCESS_STATE = Object.freeze({
  CLICKABLE: 'clickable',
  DISABLED: 'disabled',
});

// ─── CSS CONSTANTS ──────────────────────────────────────────────────────────

/** Classes CSS pour l'état actif d'un lien partenaire */
export const PARTNER_ACTIVE_CLASSES = 'text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer';

/** Classes CSS pour l'état disabled d'un lien partenaire */
export const PARTNER_DISABLED_CLASSES = 'text-gray-400 cursor-not-allowed';

/** Tooltip pour les partenaires non cliquables */
export const PARTNER_DISABLED_TOOLTIP = 'Accès non activé — aucun utilisateur associé';

/** Label pour l'état activé */
export const PARTNER_LABEL_ACTIVE = 'Accès partenaire activé';

/** Label pour l'état non activé */
export const PARTNER_LABEL_INACTIVE = 'Accès non activé';

/** Label pour distributeur (toujours actif) */
export const PARTNER_LABEL_DISTRIBUTOR = 'Distributeur — accès permanent';

// ─── CORE RULE ──────────────────────────────────────────────────────────────

/**
 * Extrait les capabilities d'un objet partenaire, quel que soit le format.
 * Gère : partner.capabilities, partner.partner_capabilities
 * 
 * @param {Object} partner
 * @returns {string[]}
 */
function getCapabilities(partner) {
  if (!partner) return [];
  return partner.capabilities || partner.partner_capabilities || [];
}

/**
 * Détermine si un partenaire est de type Distributor.
 * 
 * @param {Object} partner
 * @returns {boolean}
 */
function isDistributor(partner) {
  return getCapabilities(partner).includes('distributor');
}

// ─── PUBLIC API ─────────────────────────────────────────────────────────────

/**
 * Détermine si un partenaire est cliquable (lien vers fiche /partner/:slug).
 * 
 * PRIORITÉ:
 *   1. access_state.clickable (backend runtime — source finale)
 *   2. Calcul local (fallback si access_state absent)
 * 
 * RÈGLE:
 *   Distributor → true (toujours)
 *   Autres      → true uniquement si has_users === true
 *   null        → false
 * 
 * @param {Object} partner - Objet partenaire (distributions[], publishers[], partners[])
 * @returns {boolean}
 */
export function isPartnerClickable(partner) {
  if (!partner) return false;
  
  // PRIORITÉ 1: Backend runtime state (P1.7 — source finale)
  if (partner.access_state && typeof partner.access_state.clickable === 'boolean') {
    return partner.access_state.clickable;
  }
  
  // PRIORITÉ 2: Calcul local (fallback backward compat)
  if (isDistributor(partner)) return true;
  return partner.has_users === true;
}

/**
 * Retourne l'état d'accès complet d'un partenaire.
 * Consomme access_state backend (P1.7) si disponible, sinon calcul local.
 * 
 * @param {Object} partner
 * @returns {{ state: string, clickable: boolean, isDistributor: boolean, reason: string|null, tooltip: string|null, cssClass: string }}
 */
export function getPartnerAccessState(partner) {
  const clickable = isPartnerClickable(partner);
  const dist = isDistributor(partner);
  const reason = partner?.access_state?.reason || (dist ? 'distributor' : (clickable ? 'has_users' : 'no_users'));

  return {
    state: clickable ? PARTNER_ACCESS_STATE.CLICKABLE : PARTNER_ACCESS_STATE.DISABLED,
    clickable,
    isDistributor: dist,
    reason,
    tooltip: clickable ? null : PARTNER_DISABLED_TOOLTIP,
    cssClass: clickable ? PARTNER_ACTIVE_CLASSES : PARTNER_DISABLED_CLASSES,
  };
}

/**
 * Assertion de policy — lance une erreur en dev si un partenaire invalide
 * est utilisé dans un contexte qui requiert clickable.
 * 
 * Usage (dev-only guard) :
 *   assertPartnerPolicy(partner, 'ProductCard partner link');
 * 
 * @param {Object} partner
 * @param {string} context - Description du contexte d'appel
 * @throws {Error} en mode développement si partner manque capabilities ou has_users
 */
export function assertPartnerPolicy(partner, context = '') {
  if (process.env.NODE_ENV !== 'development') return;
  if (!partner) return;

  const caps = getCapabilities(partner);
  const hasUsersField = 'has_users' in partner;

  if (caps.length === 0 && !hasUsersField) {
    console.warn(
      `[PARTNER_POLICY VIOLATION] ${context}: partner "${partner.nom || partner.name || partner.id || '?'}" ` +
      `missing both capabilities[] and has_users. Backend enrichment may be incomplete.`
    );
  }
}

// ─── ARIA HELPERS ───────────────────────────────────────────────────────────

/**
 * Retourne le label UX officiel pour l'état d'accès d'un partenaire.
 * 
 * @param {Object} partner
 * @returns {string} Label UX normalisé
 */
export function getPartnerAccessLabel(partner) {
  if (!partner) return PARTNER_LABEL_INACTIVE;
  if (isDistributor(partner)) return PARTNER_LABEL_DISTRIBUTOR;
  if (isPartnerClickable(partner)) return PARTNER_LABEL_ACTIVE;
  return PARTNER_LABEL_INACTIVE;
}

/**
 * Retourne les props d'accessibilité pour un élément partenaire.
 * 
 * @param {Object} partner
 * @returns {Object} props à spread sur l'élément ({ 'aria-disabled', tabIndex, title, role })
 */
export function getPartnerAriaProps(partner) {
  const clickable = isPartnerClickable(partner);
  if (clickable) {
    return { role: 'link' };
  }
  return {
    'aria-disabled': 'true',
    tabIndex: -1,
    title: PARTNER_DISABLED_TOOLTIP,
  };
}
